import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Star, Check, Sparkles, Info, Save, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { previewCss, previewBgCss, presetFonts } from "@/lib/presets";

const FAV_KEY = "cm_preset_favs";
const RECENT_KEY = "cm_preset_recent";
const loadSet = (k) => { try { return new Set(JSON.parse(localStorage.getItem(k) || "[]")); } catch { return new Set(); } };
const loadArr = (k) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };

// Preload the actual FontFaces a preset showcases so card previews render in the real font.
const preloadFont = (fam) => { try { if (fam && document?.fonts) document.fonts.load(`800 20px "${fam}"`, "Aa Bb").catch(() => {}); } catch { /* noop */ } };

// Phrase Design preview: each sample word rendered with its own pattern style.
const PhrasePreview = ({ def, hover }) => {
  const words = (def.sample || "Your words here").split(/\s+/).slice(0, 6);
  const pat = def.wordPattern || [];
  return (
    <div className="relative z-10 flex max-w-full flex-wrap items-baseline justify-center gap-x-1 px-1 leading-none">
      {words.map((w, i) => {
        const p = pat[i % pat.length] || {};
        const st = { ...(def.style || {}), ...p };
        const css = previewCss({ style: st }, { family: st.fontFamily, weight: st.fontWeight, style: st.fontStyle });
        const bg = st.bgEnabled && st.bgMode === "word" ? { background: st.bgColor, padding: "1px 3px", borderRadius: 3 } : {};
        const tc = st.textCase === "upper" ? "uppercase" : st.textCase === "lower" ? "lowercase" : undefined;
        return <span key={i} className={hover ? `cm-anim-${p.animation || def.animation || "pop"}` : ""} style={{ ...css, ...bg, textTransform: tc, fontSize: `${Math.max(8, Math.min(26, 13 * (p.sizeScale || 1)))}px`, letterSpacing: st.letterSpacing ? `${Math.min(3, st.letterSpacing / 3)}px` : undefined }}>{w}</span>;
      })}
    </div>
  );
};

const PresetCard = ({ preset, applied, fav, onFav, onApply, disabled, onDelete }) => {
  const [hover, setHover] = useState(false);
  const def = preset.definition || {};
  const anim = def.animation || "pop";
  const bg = previewBgCss(def);
  const { primary, secondary } = presetFonts(def);
  useEffect(() => { preloadFont(primary.family); if (secondary) preloadFont(secondary.family); }, [primary.family, secondary]);
  return (
    <div className="group relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button type="button" onClick={(e) => { e.stopPropagation(); onFav(preset.id); }} title="Favorite" data-testid={`cpl-fav-${preset.id}`}
        className={`absolute right-1 top-1 z-30 rounded p-0.5 ${fav ? "text-[#FFD400]" : "text-[#666] opacity-0 group-hover:opacity-100"}`}><Star size={11} fill={fav ? "#FFD400" : "none"} /></button>
      {onDelete && <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(preset); }} title="Delete" data-testid={`cpl-delete-${preset.id}`}
        className="absolute right-5 top-1 z-30 rounded p-0.5 text-[#666] opacity-0 hover:text-red-400 group-hover:opacity-100"><Trash2 size={11} /></button>}
    <button type="button" data-testid={`cpl-card-${preset.slug || preset.id}`} data-preset-card={preset.slug || preset.id} disabled={disabled} onClick={() => onApply(preset)}
      title={preset.name}
      className={`relative flex w-full flex-col overflow-hidden rounded-lg border text-left transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${applied ? "border-[#00F0FF]" : "border-white/10 hover:border-[#00F0FF]/50"}`}>
      <span data-testid={`preset-card-${preset.slug || preset.id}`} className="sr-only">{preset.name}</span>
      {applied && <div className="absolute left-1 top-1 z-20 rounded-full bg-[#00F0FF] p-0.5 text-black"><Check size={10} /></div>}
      {secondary && <div className="absolute left-1 bottom-[26px] z-20 rounded bg-[#a78bfa] px-1 text-[8px] font-black text-black" data-testid={`cpl-twofont-badge-${preset.slug || preset.id}`}>2-FONT</div>}
      <div className={`relative flex items-center justify-center gap-0.5 overflow-hidden bg-[radial-gradient(circle_at_center,#1a1a1a,#0a0a0a)] px-2 ${def.wordPattern ? "h-[72px]" : "h-14"}`}>
        {bg && !def.wordPattern && <div className="absolute inset-x-2 h-8 rounded" style={bg} />}
        {def.wordPattern ? <PhrasePreview def={def} hover={hover} key={hover ? "hp" : "sp"} /> : secondary ? (
          <>
            <span key={hover ? "hA" : "sA"} className={`relative z-10 truncate text-base font-black ${hover ? `cm-anim-${anim}` : ""}`} style={previewCss(def, primary)}>Aa</span>
            <span key={hover ? "hB" : "sB"} className={`relative z-10 truncate text-base font-black ${hover ? `cm-anim-${anim}` : ""}`} style={previewCss(def, secondary)}>Bb</span>
          </>
        ) : (
          <span key={hover ? "h" : "s"} className={`relative z-10 truncate text-base font-black ${hover ? `cm-anim-${anim}` : ""}`} style={previewCss(def, primary)}>Aa</span>
        )}
      </div>
      <div className="border-t border-white/10 px-1.5 py-1">
        <div className="truncate text-[10px] font-semibold text-white">{preset.name}</div>
        <div className="truncate text-[9px] text-[#666]">{secondary ? `${primary.family} + ${secondary.family}` : (preset.group || preset.category)}</div>
      </div>
    </button>
    </div>
  );
};

export default function CaptionPresetsLibrary({ selectedCount = 0, totalCaptions = 0, onApply, appliedPresetId, projectId, onSave, refreshKey = 0 }) {
  const [presets, setPresets] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [favs, setFavs] = useState(() => loadSet(FAV_KEY));
  const [recent, setRecent] = useState(() => loadArr(RECENT_KEY));

  useEffect(() => { api.listPresets().then(setPresets).catch(() => setPresets([])); }, [refreshKey]);
  useEffect(() => {
    const reload = () => api.listPresets().then(setPresets).catch(() => {});
    window.addEventListener("cm-presets-changed", reload);
    return () => window.removeEventListener("cm-presets-changed", reload);
  }, []);
  const removePreset = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from My Text Presets?`)) return;
    try { await api.deletePreset(p.id); setPresets((ps) => ps.filter((x) => x.id !== p.id)); toast.success("Preset deleted"); } catch { toast.error("Delete failed"); }
  };

  const groups = useMemo(() => {
    const s = new Set();
    presets.forEach((p) => s.add(p.group || p.category));
    s.delete("My Text Presets"); s.delete("Phrase Designs");
    return ["All", "My Text Presets", "Phrase Designs", "Recent", "Favorites", ...[...s].sort()];
  }, [presets]);

  const shown = useMemo(() => {
    let list = presets;
    const term = q.trim().toLowerCase();
    if (term) list = list.filter((p) => [p.name, p.group, p.category, p.description].filter(Boolean).some((x) => x.toLowerCase().includes(term)));
    if (cat === "Favorites") list = list.filter((p) => favs.has(p.id));
    else if (cat === "Recent") { const order = new Map(recent.map((id, i) => [id, i])); list = list.filter((p) => order.has(p.id)).sort((a, b) => order.get(a.id) - order.get(b.id)); }
    else if (cat === "My Text Presets") list = list.filter((p) => !p.is_builtin);
    else if (cat !== "All") list = list.filter((p) => (p.group || p.category) === cat);
    return list;
  }, [presets, q, cat, favs, recent]);

  const toggleFav = (id) => setFavs((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); try { localStorage.setItem(FAV_KEY, JSON.stringify([...n])); } catch (_) {} return n; });
  const noSelection = false;

  // Where to apply is asked every time (This caption / Selected captions / All captions).
  const apply = (preset) => {
    onApply && onApply(preset);
    api.trackPreset(preset.id, projectId);
    const next = [preset.id, ...recent.filter((x) => x !== preset.id)].slice(0, 12);
    setRecent(next); try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch (_) {}
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="caption-presets-library">
      <div className="flex items-center gap-1.5 px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-wide text-[#a78bfa]">
        <Sparkles size={13} /> Caption Presets
      </div>
      <div className="px-3 pb-2 text-[10px] text-[#A1A1AA]" data-testid="cpl-hint">
        Click a preset, then choose: this caption, your selected captions, or all {totalCaptions} captions.
      </div>
      <div className="flex items-center gap-1.5 px-3 pb-2">
        <div className="flex-1" />
        <button onClick={() => onSave && onSave()} disabled={!onSave} data-testid="cpl-save-design" title={onSave ? "Save this caption's complete design (all word styles + animations) to My Text Presets" : "Select one caption to save its design"}
          className="flex shrink-0 items-center gap-1 rounded-lg border border-[#a78bfa]/40 px-2 py-1 text-[10px] font-bold text-[#c4b5fd] hover:bg-[#a78bfa]/10 disabled:opacity-40"><Save size={11} /> Save design</button>
      </div>
      <div className="px-3">
        <div className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5">
          <Search size={13} className="text-[#666]" />
          <input data-testid="cpl-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search presets…" className="w-full bg-transparent text-xs outline-none placeholder:text-[#666]" />
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto px-3 py-2" data-testid="cpl-categories">
        {groups.map((g) => (
          <button key={g} data-testid={`cpl-cat-${g}`} onClick={() => setCat(g)} className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold ${cat === g ? "bg-[#00F0FF] text-black" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{g}</button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {shown.length === 0 ? (
          <div className="flex items-center gap-1.5 py-8 text-center text-[11px] text-[#666]"><Info size={13} /> {cat === "Recent" ? "No recent presets yet." : cat === "Favorites" ? "Star presets to save them here." : cat === "My Text Presets" ? "Design a caption, then click “Save design” to keep it here." : "No presets match."}</div>
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            {shown.map((p) => <PresetCard key={p.id} preset={p} applied={appliedPresetId === p.id} fav={favs.has(p.id)} onFav={toggleFav} onApply={apply} disabled={noSelection} onDelete={p.is_builtin ? null : removePreset} />)}
          </div>
        )}
      </div>
    </div>
  );
}
